<template>
  <BaseTemplate app-name="Medical System" page-title="">
    <template #content>
      <div
        class="h-screen flex flex-col bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-100 transition-colors duration-200"
      >
        <!-- Main Content -->
        <div class="flex-1 flex overflow-hidden">
          <!-- Content Area -->
          <div class="flex-1 overflow-hidden flex flex-col">
            <!-- Header -->
            <div class="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 shadow-sm">
              <div class="flex flex-wrap justify-between items-center gap-4">
                <div class="flex items-center gap-3">
                  <h2 class="text-xl font-medium clinic-header">{{ currentView === 'list' ? 'Webhooks' : 'Configuração de Webhook' }}</h2>
                  <button
                    v-if="currentView === 'form'"
                    class="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    @click="goToList"
                    aria-label="Voltar para lista"
                  >
                    <ArrowLeftIcon class="w-5 h-5" />
                  </button>
                </div>
                <div class="flex items-center gap-2">
                  <button
                    v-if="currentView === 'list'"
                    class="px-4 py-2 clinic-btn rounded-md transition-colors shadow-sm flex items-center gap-2"
                    @click="openNewWebhookForm"
                  >
                    <PlusIcon class="w-4 h-4" />
                    <span>Novo Webhook</span>
                  </button>
                  <button
                    v-if="currentView === 'form'"
                    class="px-4 py-2 clinic-btn rounded-md transition-colors shadow-sm flex items-center gap-2"
                    @click="saveWebhook"
                    :disabled="!isFormValid"
                  >
                    <SaveIcon class="w-4 h-4" />
                    <span>{{ editMode ? 'Atualizar' : 'Salvar' }} Webhook</span>
                  </button>
                </div>
              </div>
            </div>

            <!-- Webhook List View -->
            <div v-if="currentView === 'list'" class="flex-1 overflow-y-auto p-4 bg-gray-50 dark:bg-gray-900">
              <div class="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-300 dark:border-gray-700 p-4">
                <!-- Search and Filter -->
                <div class="mb-4 flex flex-wrap gap-4">
                  <div class="flex-1 min-w-[240px]">
                    <div class="relative">
                      <input
                        type="text"
                        v-model="searchQuery"
                        placeholder="Buscar webhooks..."
                        class="w-full px-3 py-2 pl-10 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                      <SearchIcon class="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    </div>
                  </div>
                  <div class="w-full md:w-auto flex gap-2">
                    <div class="relative">
                      <select
                        v-model="filterEvent"
                        class="h-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent appearance-none pr-8"
                      >
                        <option value="">Todos os eventos</option>
                        <option value="patient.created">Paciente Criado</option>
                        <option value="patient.updated">Paciente Atualizado</option>
                        <option value="appointment.created">Consulta Agendada</option>
                        <option value="appointment.updated">Consulta Atualizada</option>
                        <option value="appointment.canceled">Consulta Cancelada</option>
                        <option value="invoice.generated">Fatura Gerada</option>
                        <option value="payment.received">Pagamento Recebido</option>
                      </select>
                      <div class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                        <ChevronDownIcon class="w-4 h-4 text-gray-500" />
                      </div>
                    </div>
                    <button
                      @click="resetFilters"
                      class="px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                      <RefreshCwIcon class="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <!-- Webhooks Table -->
                <div class="overflow-x-auto">
                  <table class="w-full border-collapse">
                    <thead>
                      <tr class="bg-gray-50 dark:bg-gray-900/50 text-left">
                        <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                          ID
                        </th>
                        <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                          Nome
                        </th>
                        <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                          URL
                        </th>
                        <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                          Eventos
                        </th>
                        <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                          Taxa de Sucesso
                        </th>
                        <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                          Status
                        </th>
                        <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="filteredWebhooks.length === 0">
                        <td colspan="7" class="px-4 py-6 text-center text-gray-500 dark:text-gray-400">
                          Nenhum webhook encontrado
                        </td>
                      </tr>
                      <tr
                        v-for="webhook in filteredWebhooks"
                        :key="webhook.id"
                        class="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                      >
                        <td class="px-4 py-3 text-sm">{{ webhook.id }}</td>
                        <td class="px-4 py-3">
                          <div class="flex items-center gap-3">
                            <div
                              class="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium flex-shrink-0"
                              :style="{ backgroundColor: getWebhookColor(webhook.id) }"
                            >
                              <WebhookIcon class="w-4 h-4" />
                            </div>
                            <div class="text-sm font-medium">{{ webhook.name }}</div>
                          </div>
                        </td>
                        <td class="px-4 py-3 text-sm">
                          <div class="max-w-[200px] truncate">{{ webhook.url }}</div>
                        </td>
                        <td class="px-4 py-3 text-sm">
                          <div class="flex flex-wrap gap-1">
                            <span
                              v-for="(event, index) in webhook.events.slice(0, 2)"
                              :key="index"
                              class="px-2 py-0.5 text-xs rounded-full bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                            >
                              {{ getEventLabel(event) }}
                            </span>
                            <span
                              v-if="webhook.events.length > 2"
                              class="px-2 py-0.5 text-xs rounded-full bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                            >
                              +{{ webhook.events.length - 2 }}
                            </span>
                          </div>
                        </td>
                        <td class="px-4 py-3 text-sm">
                          <div class="flex items-center gap-2">
                            <div class="w-full max-w-[100px] bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                              <div
                                class="h-2.5 rounded-full"
                                :class="{
                                  'bg-emerald-500': webhook.successRate >= 90,
                                  'bg-yellow-500': webhook.successRate >= 70 && webhook.successRate < 90,
                                  'bg-red-500': webhook.successRate < 70
                                }"
                                :style="{ width: `${webhook.successRate}%` }"
                              ></div>
                            </div>
                            <span>{{ webhook.successRate }}%</span>
                          </div>
                        </td>
                        <td class="px-4 py-3 text-sm">
                          <span
                            class="px-2 py-1 text-xs rounded-full"
                            :class="{
                              'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-400': webhook.active,
                              'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400': !webhook.active
                            }"
                          >
                            {{ webhook.active ? 'Ativo' : 'Inativo' }}
                          </span>
                        </td>
                        <td class="px-4 py-3 text-sm">
                          <div class="flex gap-2">
                            <button
                              @click="editWebhook(webhook)"
                              class="p-1 text-gray-500 hover:text-emerald-600 dark:text-gray-400 dark:hover:text-emerald-400"
                              title="Editar"
                            >
                              <EditIcon class="w-4 h-4" />
                            </button>
                            <button
                              @click="confirmDeleteWebhook(webhook)"
                              class="p-1 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                              title="Excluir"
                            >
                              <TrashIcon class="w-4 h-4" />
                            </button>
                            <button
                              @click="testWebhook(webhook)"
                              class="p-1 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                              title="Testar Webhook"
                            >
                              <SendIcon class="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Pagination -->
                <div class="mt-4 flex justify-between items-center">
                  <div class="text-sm text-gray-500 dark:text-gray-400">
                    Mostrando {{ filteredWebhooks.length }} de {{ webhooks.length }} webhooks
                  </div>
                  <div class="flex gap-2">
                    <button
                      class="px-3 py-1 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
                      disabled
                    >
                      Anterior
                    </button>
                    <button
                      class="px-3 py-1 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
                      disabled
                    >
                      Próximo
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Webhook Form View with Tabs -->
            <div v-if="currentView === 'form'" class="flex-1 overflow-y-auto p-4 bg-gray-50 dark:bg-gray-900">
              <div class="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-300 dark:border-gray-700">
                <!-- Tabs Navigation -->
                <div class="border-b border-gray-200 dark:border-gray-700">
                  <div class="flex overflow-x-auto">
                    <button
                      v-for="tab in tabs"
                      :key="tab.id"
                      @click="activeTab = tab.id"
                      class="px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors focus:outline-none"
                      :class="activeTab === tab.id ? 'clinic-tab-active' : 'clinic-tab-inactive'"
                    >
                      <div class="flex items-center gap-2">
                        <component :is="tab.icon" class="w-4 h-4" />
                        {{ tab.name }}
                      </div>
                    </button>
                  </div>
                </div>

                <!-- Tab Content -->
                <div class="p-6">
                  <!-- Basic Information Tab -->
                  <div v-if="activeTab === 'basic'" class="space-y-4">
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <!-- ID do Webhook -->
                      <div>
                        <label class="block text-sm font-medium mb-1">ID do Webhook</label>
                        <div class="relative">
                          <input
                            type="text"
                            v-model="currentWebhook.id"
                            placeholder="Gerado automaticamente"
                            disabled
                            class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                          />
                        </div>
                      </div>

                      <!-- Nome do Webhook -->
                      <div class="md:col-span-2">
                        <label for="name" class="block text-sm font-medium mb-1">Nome do Webhook*</label>
                        <input
                          id="name"
                          type="text"
                          v-model="currentWebhook.name"
                          required
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div>
                      <label for="description" class="block text-sm font-medium mb-1">Descrição</label>
                      <textarea
                        id="description"
                        v-model="currentWebhook.description"
                        rows="2"
                        class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      ></textarea>
                    </div>

                    <div>
                      <label for="url" class="block text-sm font-medium mb-1">URL de Destino*</label>
                      <input
                        id="url"
                        type="url"
                        v-model="currentWebhook.url"
                        placeholder="https://api.exemplo.com/webhook"
                        required
                        class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <!-- Status -->
                      <div>
                        <label for="active" class="block text-sm font-medium mb-1">Status</label>
                        <div class="relative">
                          <select
                            id="active"
                            v-model="currentWebhook.active"
                            class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent appearance-none pr-8"
                          >
                            <option :value="true">Ativo</option>
                            <option :value="false">Inativo</option>
                          </select>
                          <div class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                            <ChevronDownIcon class="w-4 h-4 text-gray-500" />
                          </div>
                        </div>
                      </div>

                      <!-- Formato -->
                      <div>
                        <label for="format" class="block text-sm font-medium mb-1">Formato da Carga</label>
                        <div class="relative">
                          <select
                            id="format"
                            v-model="currentWebhook.format"
                            class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent appearance-none pr-8"
                          >
                            <option value="json">JSON</option>
                            <option value="xml">XML</option>
                            <option value="form">Form URL Encoded</option>
                          </select>
                          <div class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                            <ChevronDownIcon class="w-4 h-4 text-gray-500" />
                          </div>
                        </div>
                      </div>

                      <!-- Timeout -->
                      <div>
                        <label for="timeout" class="block text-sm font-medium mb-1">Timeout (segundos)</label>
                        <input
                          id="timeout"
                          type="number"
                          v-model="currentWebhook.timeout"
                          min="1"
                          max="60"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>

                  <!-- Events Tab -->
                  <div v-if="activeTab === 'events'" class="space-y-4">
                    <div class="mb-4">
                      <div class="flex justify-between items-center mb-2">
                        <label class="block text-sm font-medium">Eventos*</label>
                        <div class="flex gap-2">
                          <button
                            @click="selectAllEvents"
                            class="text-xs text-gray-500 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400"
                          >
                            Selecionar Todos
                          </button>
                          <span class="text-gray-500">|</span>
                          <button
                            @click="deselectAllEvents"
                            class="text-xs text-gray-500 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400"
                          >
                            Limpar Seleção
                          </button>
                        </div>
                      </div>
                      <div class="space-y-3 bg-gray-50 dark:bg-gray-900/50 p-4 rounded-md border border-gray-200 dark:border-gray-700">
                        <div class="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Pacientes</div>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <label class="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              v-model="currentWebhook.events"
                              value="patient.created"
                              class="rounded text-emerald-500 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <span class="text-sm">Paciente Criado</span>
                          </label>
                          <label class="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              v-model="currentWebhook.events"
                              value="patient.updated"
                              class="rounded text-emerald-500 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <span class="text-sm">Paciente Atualizado</span>
                          </label>
                        </div>

                        <div class="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2 mt-4">Consultas</div>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <label class="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              v-model="currentWebhook.events"
                              value="appointment.created"
                              class="rounded text-emerald-500 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <span class="text-sm">Consulta Agendada</span>
                          </label>
                          <label class="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              v-model="currentWebhook.events"
                              value="appointment.updated"
                              class="rounded text-emerald-500 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <span class="text-sm">Consulta Atualizada</span>
                          </label>
                          <label class="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              v-model="currentWebhook.events"
                              value="appointment.canceled"
                              class="rounded text-emerald-500 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <span class="text-sm">Consulta Cancelada</span>
                          </label>
                          <label class="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              v-model="currentWebhook.events"
                              value="appointment.completed"
                              class="rounded text-emerald-500 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <span class="text-sm">Consulta Concluída</span>
                          </label>
                        </div>

                        <div class="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2 mt-4">Financeiro</div>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <label class="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              v-model="currentWebhook.events"
                              value="invoice.generated"
                              class="rounded text-emerald-500 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <span class="text-sm">Fatura Gerada</span>
                          </label>
                          <label class="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              v-model="currentWebhook.events"
                              value="payment.received"
                              class="rounded text-emerald-500 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <span class="text-sm">Pagamento Recebido</span>
                          </label>
                        </div>
                      </div>
                      <p 
                        v-if="currentWebhook.events.length === 0" 
                        class="text-sm text-red-500 mt-2"
                      >
                        Selecione pelo menos um evento para este webhook
                      </p>
                    </div>
                  </div>

                  <!-- Configuration Tab -->
                  <div v-if="activeTab === 'config'" class="space-y-4">
                    <div>
                      <label class="block text-sm font-medium mb-3">Headers HTTP</label>
                      <div class="space-y-2">
                        <div 
                          v-for="(header, index) in currentWebhook.headers" 
                          :key="index" 
                          class="flex gap-2"
                        >
                          <input
                            type="text"
                            v-model="header.key"
                            placeholder="Nome do Header"
                            class="flex-1 px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                          />
                          <input
                            type="text"
                            v-model="header.value"
                            placeholder="Valor do Header"
                            class="flex-1 px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                          />
                          <button
                            @click="removeHeader(index)"
                            class="p-2 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                          >
                            <XIcon class="w-5 h-5" />
                          </button>
                        </div>
                        <div>
                          <button
                            @click="addHeader"
                            class="mt-2 text-sm text-emerald-600 dark:text-emerald-400 hover:text-emerald-800 dark:hover:text-emerald-300 flex items-center gap-1"
                          >
                            <PlusCircleIcon class="w-4 h-4" />
                            <span>Adicionar Header</span>
                          </button>
                        </div>
                      </div>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label for="secret" class="block text-sm font-medium mb-1">Chave Secreta</label>
                        <div class="relative">
                          <input
                            id="secret"
                            :type="showSecret ? 'text' : 'password'"
                            v-model="currentWebhook.secret"
                            placeholder="Chave para assinatura dos payloads"
                            class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent pr-10"
                          />
                          <button
                            type="button"
                            class="absolute inset-y-0 right-0 px-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                            @click="showSecret = !showSecret"
                          >
                            <EyeIcon v-if="!showSecret" class="w-4 h-4" />
                            <EyeOffIcon v-else class="w-4 h-4" />
                          </button>
                        </div>
                        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Usada para assinar os payloads e verificar autenticidade
                        </p>
                      </div>

                      <div>
                        <label for="retryAttempts" class="block text-sm font-medium mb-1">Tentativas de Reenvio</label>
                        <input
                          id="retryAttempts"
                          type="number"
                          v-model="currentWebhook.retryAttempts"
                          min="0"
                          max="10"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        />
                        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Número de tentativas em caso de falha (0 = sem retry)
                        </p>
                      </div>
                    </div>

                    <div>
                      <label for="defaultPayload" class="block text-sm font-medium mb-1">Payload de Teste</label>
                      <textarea
                        id="defaultPayload"
                        v-model="currentWebhook.defaultPayload"
                        rows="5"
                        class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent font-mono"
                        placeholder='{"event": "test", "timestamp": "2023-08-15T14:30:00Z", "data": {"message": "Teste de webhook"}}'
                      ></textarea>
                      <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Payload usado no botão "Testar Webhook" (formato JSON)
                      </p>
                    </div>
                  </div>

                  <!-- History Tab -->
                  <div v-if="activeTab === 'history'" class="space-y-4">
                    <div 
                      v-if="!editMode || !currentWebhook.history || currentWebhook.history.length === 0" 
                      class="text-center py-8 text-gray-500 dark:text-gray-400"
                    >
                      <ClockIcon class="w-10 h-10 mx-auto mb-3 opacity-40" />
                      <p>Não há histórico disponível para este webhook.</p>
                      <p class="text-sm mt-1" v-if="!editMode">Salve o webhook primeiro para visualizar o histórico.</p>
                    </div>

                    <div v-else class="space-y-4">
                      <div class="flex justify-between items-center mb-2">
                        <h3 class="text-sm font-medium">Últimas Chamadas</h3>
                        <button
                          @click="clearHistory"
                          class="text-xs text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 flex items-center gap-1"
                        >
                          <TrashIcon class="w-3 h-3" />
                          <span>Limpar Histórico</span>
                        </button>
                      </div>

                      <div class="space-y-3">
                        <div 
                          v-for="(entry, index) in currentWebhook.history" 
                          :key="index" 
                          class="bg-gray-50 dark:bg-gray-900/50 p-3 rounded-md border border-gray-200 dark:border-gray-700"
                        >
                          <div class="flex justify-between items-start mb-2">
                            <div class="flex items-center gap-2">
                              <span
                                class="w-2 h-2 rounded-full"
                                :class="{
                                  'bg-emerald-500': entry.status >= 200 && entry.status < 300,
                                  'bg-yellow-500': entry.status >= 300 && entry.status < 400,
                                  'bg-red-500': entry.status >= 400 || entry.status === 0
                                }"
                              ></span>
                              <span 
                                class="font-medium"
                                :class="{
                                  'text-emerald-600 dark:text-emerald-400': entry.status >= 200 && entry.status < 300,
                                  'text-yellow-600 dark:text-yellow-400': entry.status >= 300 && entry.status < 400,
                                  'text-red-600 dark:text-red-400': entry.status >= 400 || entry.status === 0
                                }"
                              >
                                {{ entry.status === 0 ? 'Falha de Conexão' : `HTTP ${entry.status}` }}
                              </span>
                              <span class="text-sm text-gray-500 dark:text-gray-400">{{ entry.event }}</span>
                            </div>
                            <span class="text-xs text-gray-500 dark:text-gray-400">{{ formatDate(entry.timestamp) }}</span>
                          </div>
                          <div class="text-xs text-gray-600 dark:text-gray-300 mt-1">
                            <div><span class="font-medium">Duração:</span> {{ entry.duration }}ms</div>
                            <div v-if="entry.error"><span class="font-medium">Erro:</span> {{ entry.error }}</div>
                          </div>
                          <div 
                            v-if="expandedHistoryEntry === index" 
                            class="mt-2 text-xs bg-gray-100 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700 font-mono overflow-x-auto"
                          >
                            <pre>{{ entry.payload }}</pre>
                          </div>
                          <button
                            @click="toggleHistoryEntry(index)"
                            class="text-xs text-emerald-600 dark:text-emerald-400 mt-2 flex items-center gap-1"
                          >
                            <span>{{ expandedHistoryEntry === index ? 'Ocultar Payload' : 'Ver Payload' }}</span>
                            <ChevronDownIcon v-if="expandedHistoryEntry !== index" class="w-3 h-3" />
                            <ChevronUpIcon v-else class="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Form Actions -->
                  <div class="mt-8 flex justify-between items-center">
                    <div class="flex gap-2">

                    </div>
                    <div class="flex gap-3">
                      <button
                        class="px-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md transition-colors"
                        @click="goToList"
                      >
                        Cancelar
                      </button>
                      <button
                        class="px-6 py-2 clinic-btn rounded-md transition-colors shadow-sm disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
                        @click="saveWebhook"
                        :disabled="!isFormValid"
                      >
                        {{ editMode ? 'Atualizar' : 'Salvar' }} Webhook
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Success Modal -->
            <div
              class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
              v-if="showSuccessModal"
              @click.self="closeSuccessModal"
            >
              <div
                class="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto animate-fadeIn"
              >
                <div class="p-6 text-center">
                  <div
                    class="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 mx-auto mb-4"
                  >
                    <CheckIcon class="w-8 h-8" />
                  </div>
                  <h3 class="text-xl font-medium mb-2">{{ editMode ? 'Webhook Atualizado' : 'Webhook Cadastrado' }}</h3>
                  <p class="text-gray-600 dark:text-gray-400 mb-6">
                    {{ editMode ? 'O webhook foi atualizado com sucesso.' : 'O webhook foi cadastrado com sucesso no sistema.' }}
                  </p>
                  <div class="flex justify-center gap-3">
                    <button
                      class="px-4 py-2 clinic-btn rounded-md transition-colors shadow-sm"
                      @click="closeSuccessModal"
                    >
                      OK
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Delete Confirmation Modal -->
            <div
              class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
              v-if="showDeleteModal"
              @click.self="showDeleteModal = false"
            >
              <div
                class="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto animate-fadeIn"
              >
                <div class="p-6">
                  <div
                    class="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center text-red-600 dark:text-red-400 mx-auto mb-4"
                  >
                    <AlertTriangleIcon class="w-8 h-8" />
                  </div>
                  <h3 class="text-xl font-medium mb-2 text-center">Confirmar Exclusão</h3>
                  <p class="text-gray-600 dark:text-gray-400 mb-6 text-center">
                    Tem certeza que deseja excluir o webhook <span class="font-medium">{{ webhookToDelete?.name }}</span>?
                    <br />Esta ação não pode ser desfeita.
                  </p>
                  <div class="flex justify-end gap-3">
                    <button
                      class="px-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md transition-colors"
                      @click="showDeleteModal = false"
                    >
                      Cancelar
                    </button>
                    <button
                      class="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md transition-colors shadow-sm"
                      @click="deleteWebhook"
                    >
                      Excluir
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Test Webhook Modal -->
            <div
              class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
              v-if="showTestModal"
              @click.self="showTestModal = false"
            >
              <div
                class="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto animate-fadeIn"
              >
                <div class="p-6">
                  <h3 class="text-xl font-medium mb-4">Testar Webhook</h3>
                  
                  <div v-if="testStatus === 'preparing'" class="space-y-4">
                    <div>
                      <label for="testEvent" class="block text-sm font-medium mb-1">Evento</label>
                      <div class="relative">
                        <select
                          id="testEvent"
                          v-model="testEvent"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent appearance-none pr-8"
                        >
                          <option v-for="event in webhookToTest.events" :key="event" :value="event">
                            {{ getEventLabel(event) }}
                          </option>
                        </select>
                        <div class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                          <ChevronDownIcon class="w-4 h-4 text-gray-500" />
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <label for="testPayload" class="block text-sm font-medium mb-1">Payload</label>
                      <textarea
                        id="testPayload"
                        v-model="testPayload"
                        rows="8"
                        class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent font-mono"
                      ></textarea>
                    </div>
                    
                    <div class="flex justify-end gap-3 mt-6">
                      <button
                        class="px-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md transition-colors"
                        @click="showTestModal = false"
                      >
                        Cancelar
                      </button>
                      <button
                        class="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-md transition-colors shadow-sm"
                        @click="executeTestWebhook"
                      >
                        Executar Teste
                      </button>
                    </div>
                  </div>
                  
                  <div v-if="testStatus === 'loading'" class="py-8 text-center">
                    <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto"></div>
                    <p class="mt-4 text-gray-600 dark:text-gray-400">Enviando requisição...</p>
                  </div>
                  
                  <div v-if="testStatus === 'success'" class="py-4">
                    <div
                      class="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 mx-auto mb-4"
                    >
                      <CheckIcon class="w-8 h-8" />
                    </div>
                    <h4 class="text-lg font-medium text-center mb-2">Webhook Enviado com Sucesso</h4>
                    <div class="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-md border border-gray-200 dark:border-gray-700 mt-4">
                      <div class="flex justify-between mb-2">
                        <span class="text-sm font-medium">Status:</span>
                        <span class="text-sm text-emerald-600 dark:text-emerald-400">
                          HTTP {{ testResult.status }}
                        </span>
                      </div>
                      <div class="flex justify-between mb-2">
                        <span class="text-sm font-medium">Tempo:</span>
                        <span class="text-sm">{{ testResult.duration }}ms</span>
                      </div>
                      <div class="mt-3">
                        <div class="text-sm font-medium mb-1">Resposta:</div>
                        <div class="bg-white dark:bg-gray-800 p-3 rounded font-mono text-xs overflow-x-auto max-h-40 overflow-y-auto">
                          <pre>{{ testResult.response }}</pre>
                        </div>
                      </div>
                    </div>
                    <div class="flex justify-center mt-6">
                      <button
                        class="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-md transition-colors shadow-sm"
                        @click="showTestModal = false"
                      >
                        Fechar
                      </button>
                    </div>
                  </div>
                  
                  <div v-if="testStatus === 'error'" class="py-4">
                    <div
                      class="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center text-red-600 dark:text-red-400 mx-auto mb-4"
                    >
                      <AlertCircleIcon class="w-8 h-8" />
                    </div>
                    <h4 class="text-lg font-medium text-center mb-2">Falha ao Enviar Webhook</h4>
                    <div class="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-md border border-gray-200 dark:border-gray-700 mt-4">
                      <div class="text-red-600 dark:text-red-400 text-sm font-medium mb-2">
                        {{ testResult.error || 'Erro de conexão ou timeout' }}
                      </div>
                      <div v-if="testResult.status" class="flex justify-between mb-2">
                        <span class="text-sm font-medium">Status:</span>
                        <span class="text-sm text-red-600 dark:text-red-400">
                          HTTP {{ testResult.status }}
                        </span>
                      </div>
                      <div v-if="testResult.duration" class="flex justify-between mb-2">
                        <span class="text-sm font-medium">Tempo:</span>
                        <span class="text-sm">{{ testResult.duration }}ms</span>
                      </div>
                      <div v-if="testResult.response" class="mt-3">
                        <div class="text-sm font-medium mb-1">Resposta:</div>
                        <div class="bg-white dark:bg-gray-800 p-3 rounded font-mono text-xs overflow-x-auto max-h-40 overflow-y-auto">
                          <pre>{{ testResult.response }}</pre>
                        </div>
                      </div>
                    </div>
                    <div class="flex justify-center mt-6 gap-3">
                      <button
                        class="px-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md transition-colors"
                        @click="showTestModal = false"
                      >
                        Fechar
                      </button>
                      <button
                        class="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-md transition-colors shadow-sm"
                        @click="testStatus = 'preparing'"
                      >
                        Tentar Novamente
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </BaseTemplate>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import BaseTemplate from '../templates/BaseTemplate.vue';
import { 
  Plus, 
  ChevronDown,
  ChevronUp,
  Check, 
  Search, 
  Edit, 
  Trash, 
  Save, 
  ArrowLeft, 
  RefreshCw, 
  AlertTriangle,
  FileText,
  Bell,
  Settings,
  Clock,
  Webhook,
  Send,
  Eye,
  EyeOff,
  X,
  PlusCircle,
  AlertCircle
} from 'lucide-vue-next';
import { useClinicStore } from '@/stores/settings/clinicStore';

// Icons
const PlusIcon = Plus;
const ChevronDownIcon = ChevronDown;
const ChevronUpIcon = ChevronUp;
const CheckIcon = Check;
const SearchIcon = Search;
const EditIcon = Edit;
const TrashIcon = Trash;
const SaveIcon = Save;
const ArrowLeftIcon = ArrowLeft;
const RefreshCwIcon = RefreshCw;
const AlertTriangleIcon = AlertTriangle;
const FileTextIcon = FileText;
const BellIcon = Bell;
const SettingsIcon = Settings;
const ClockIcon = Clock;
const WebhookIcon = Webhook;
const SendIcon = Send;
const EyeIcon = Eye;
const EyeOffIcon = EyeOff;
const XIcon = X;
const PlusCircleIcon = PlusCircle;
const AlertCircleIcon = AlertCircle;

// Estado
const currentView = ref('list'); // 'list' ou 'form'
const showSuccessModal = ref(false);
const showDeleteModal = ref(false);
const showTestModal = ref(false);
const editMode = ref(false);
const webhookToDelete = ref(null);
const webhookToTest = ref(null);
const searchQuery = ref('');
const filterEvent = ref('');
const activeTab = ref('basic');
const showSecret = ref(false);
const expandedHistoryEntry = ref(null);

// Teste de webhook
const testStatus = ref('preparing'); // 'preparing', 'loading', 'success', 'error'
const testEvent = ref('');
const testPayload = ref('');
const testResult = ref({});

// Definição das abas
const tabs = [
  { id: 'basic', name: 'Informações Básicas', icon: FileTextIcon },
  { id: 'events', name: 'Eventos', icon: BellIcon },
  { id: 'config', name: 'Configuração', icon: SettingsIcon },
  { id: 'history', name: 'Histórico', icon: ClockIcon }
];

// Modelo de webhook vazio
const emptyWebhook = {
  id: 'Automático',
  name: '',
  description: '',
  url: '',
  active: true,
  events: [],
  format: 'json',
  timeout: 10,
  secret: '',
  retryAttempts: 3,
  headers: [
    { key: 'Content-Type', value: 'application/json' }
  ],
  defaultPayload: JSON.stringify({
    event: "test",
    timestamp: new Date().toISOString(),
    data: { message: "Teste de webhook" }
  }, null, 2),
  history: [],
  successRate: 100
};

// Webhook atual para edição/criação
const currentWebhook = ref({ ...emptyWebhook });

// Lista de webhooks
const webhooks = ref([
  {
    id: 'WH0001',
    name: 'Notificação de Pacientes',
    description: 'Envia notificações para o sistema externo quando pacientes são criados ou atualizados',
    url: 'https://api.sistemaprincipal.com/webhooks/pacientes',
    active: true,
    events: ['patient.created', 'patient.updated'],
    format: 'json',
    timeout: 10,
    secret: 'sk_12345_notificacaopacientes',
    retryAttempts: 3,
    headers: [
      { key: 'Content-Type', value: 'application/json' },
      { key: 'X-API-Key', value: 'abc123456789' }
    ],
    defaultPayload: JSON.stringify({
      event: "patient.created",
      timestamp: "2023-08-10T15:30:00Z",
      data: { id: "PAT1234", name: "João Silva" }
    }, null, 2),
    history: [
      {
        timestamp: new Date(Date.now() - 1000 * 60 * 5),
        event: 'patient.created',
        status: 200,
        duration: 320,
        payload: JSON.stringify({
          event: "patient.created",
          timestamp: "2023-08-10T15:30:00Z",
          data: { id: "PAT1234", name: "João Silva" }
        }, null, 2)
      },
      {
        timestamp: new Date(Date.now() - 1000 * 60 * 30),
        event: 'patient.updated',
        status: 200,
        duration: 285,
        payload: JSON.stringify({
          event: "patient.updated",
          timestamp: "2023-08-10T15:00:00Z",
          data: { id: "PAT1234", name: "João Silva", email: "joao@email.com" }
        }, null, 2)
      }
    ],
    successRate: 100
  },
  {
    id: 'WH0002',
    name: 'Integração com Calendário',
    description: 'Envia atualizações de consultas para o sistema de calendário',
    url: 'https://calendar-api.exemplo.com/webhooks/medical',
    active: true,
    events: ['appointment.created', 'appointment.updated', 'appointment.canceled'],
    format: 'json',
    timeout: 5,
    secret: 'sk_67890_calendariointegrado',
    retryAttempts: 5,
    headers: [
      { key: 'Content-Type', value: 'application/json' },
      { key: 'Authorization', value: 'Bearer token123456' },
      { key: 'Authorization', value: 'Bearer token123456' }
    ],
    defaultPayload: JSON.stringify({
      event: "appointment.created",
      timestamp: "2023-08-12T10:15:00Z",
      data: { id: "APP5678", patientId: "PAT1234", date: "2023-08-15T14:30:00Z" }
    }, null, 2),
    history: [
      {
        timestamp: new Date(Date.now() - 1000 * 60 * 15),
        event: 'appointment.created',
        status: 201,
        duration: 175,
        payload: JSON.stringify({
          event: "appointment.created",
          timestamp: "2023-08-12T10:15:00Z",
          data: { id: "APP5678", patientId: "PAT1234", date: "2023-08-15T14:30:00Z" }
        }, null, 2)
      },
      {
        timestamp: new Date(Date.now() - 1000 * 60 * 45),
        event: 'appointment.canceled',
        status: 400,
        duration: 210,
        error: "Invalid appointment ID",
        payload: JSON.stringify({
          event: "appointment.canceled",
          timestamp: "2023-08-12T09:45:00Z",
          data: { id: "INVALID", reason: "Patient request" }
        }, null, 2)
      }
    ],
    successRate: 85
  },
  {
    id: 'WH0003',
    name: 'Integração Financeira',
    description: 'Envia informações financeiras para o sistema contábil',
    url: 'https://finance.exemplo.org/webhooks/receive',
    active: false,
    events: ['invoice.generated', 'payment.received'],
    format: 'json',
    timeout: 15,
    secret: 'sk_finance_9876543210',
    retryAttempts: 3,
    headers: [
      { key: 'Content-Type', value: 'application/json' },
      { key: 'X-Source', value: 'medical-system' }
    ],
    defaultPayload: JSON.stringify({
      event: "invoice.generated",
      timestamp: "2023-08-05T16:45:00Z",
      data: { id: "INV2023001", patientId: "PAT1234", amount: 150.00 }
    }, null, 2),
    history: [
      {
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2),
        event: 'invoice.generated',
        status: 0,
        duration: 30000,
        error: "Timeout after 30s",
        payload: JSON.stringify({
          event: "invoice.generated",
          timestamp: "2023-08-05T16:45:00Z",
          data: { id: "INV2023001", patientId: "PAT1234", amount: 150.00 }
        }, null, 2)
      }
    ],
    successRate: 65
  }
]);

// Computed properties
const filteredWebhooks = computed(() => {
  let result = [...webhooks.value];
  
  // Filtrar por termo de busca
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    result = result.filter(webhook => 
      webhook.name.toLowerCase().includes(query) ||
      webhook.id.toLowerCase().includes(query) ||
      webhook.description.toLowerCase().includes(query) ||
      webhook.url.toLowerCase().includes(query)
    );
  }
  
  // Filtrar por evento
  if (filterEvent.value) {
    result = result.filter(webhook => webhook.events.includes(filterEvent.value));
  }
  
  return result;
});

const isFormValid = computed(() => {
  return (
    currentWebhook.value.name.trim() !== '' &&
    currentWebhook.value.url.trim() !== '' &&
    currentWebhook.value.events.length > 0
  );
});

// Métodos
function openNewWebhookForm() {
  currentWebhook.value = JSON.parse(JSON.stringify(emptyWebhook));
  editMode.value = false;
  activeTab.value = 'basic'; // Reset to first tab
  currentView.value = 'form';
}

function editWebhook(webhook) {
  currentWebhook.value = JSON.parse(JSON.stringify(webhook)); // Deep copy
  editMode.value = true;
  activeTab.value = 'basic'; // Reset to first tab
  currentView.value = 'form';
}

function goToList() {
  currentView.value = 'list';
}

function saveWebhook() {
  if (!isFormValid.value) return;
  
  if (editMode.value) {
    // Atualizar webhook existente
    const index = webhooks.value.findIndex(w => w.id === currentWebhook.value.id);
    if (index !== -1) {
      webhooks.value[index] = JSON.parse(JSON.stringify(currentWebhook.value));
    }
  } else {
    // Criar novo webhook
    const newWebhook = JSON.parse(JSON.stringify(currentWebhook.value));
    newWebhook.id = generateWebhookId();
    // Adicionar histórico vazio
    newWebhook.history = [];
    webhooks.value.push(newWebhook);
  }
  
  // Mostrar modal de sucesso
  showSuccessModal.value = true;
  
  // Em um caso real, você enviaria os dados para uma API
  console.log('Webhook salvo:', currentWebhook.value);
}

function closeSuccessModal() {
  showSuccessModal.value = false;
  currentView.value = 'list';
}

function confirmDeleteWebhook(webhook) {
  webhookToDelete.value = webhook;
  showDeleteModal.value = true;
}

function deleteWebhook() {
  if (!webhookToDelete.value) return;
  
  const index = webhooks.value.findIndex(w => w.id === webhookToDelete.value.id);
  if (index !== -1) {
    webhooks.value.splice(index, 1);
  }
  
  showDeleteModal.value = false;
  webhookToDelete.value = null;
}

function resetFilters() {
  searchQuery.value = '';
  filterEvent.value = '';
}

function generateWebhookId() {
  // Simulando a geração de um ID de webhook
  const prefix = 'WH';
  const lastId = webhooks.value.length > 0 
    ? parseInt(webhooks.value[webhooks.value.length - 1].id.replace('WH', '')) 
    : 0;
  const newId = (lastId + 1).toString().padStart(4, '0');
  return `${prefix}${newId}`;
}

function getWebhookColor(id) {
  const colors = ['#10b981', '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#06b6d4', '#84cc16'];
  const index = parseInt(id.replace(/\D/g, '')) % colors.length;
  return colors[index];
}

function getEventLabel(eventCode) {
  const eventLabels = {
    'patient.created': 'Paciente Criado',
    'patient.updated': 'Paciente Atualizado',
    'appointment.created': 'Consulta Agendada',
    'appointment.updated': 'Consulta Atualizada',
    'appointment.canceled': 'Consulta Cancelada',
    'appointment.completed': 'Consulta Concluída',
    'invoice.generated': 'Fatura Gerada',
    'payment.received': 'Pagamento Recebido'
  };
  
  return eventLabels[eventCode] || eventCode;
}

function formatDate(date) {
  if (!date) return '';
  
  const d = new Date(date);
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function addHeader() {
  currentWebhook.value.headers.push({ key: '', value: '' });
}

function removeHeader(index) {
  currentWebhook.value.headers.splice(index, 1);
}

function selectAllEvents() {
  currentWebhook.value.events = [
    'patient.created', 'patient.updated',
    'appointment.created', 'appointment.updated', 'appointment.canceled', 'appointment.completed',
    'invoice.generated', 'payment.received'
  ];
}

function deselectAllEvents() {
  currentWebhook.value.events = [];
}

function toggleHistoryEntry(index) {
  expandedHistoryEntry.value = expandedHistoryEntry.value === index ? null : index;
}

function clearHistory() {
  if (currentWebhook.value && currentWebhook.value.history) {
    currentWebhook.value.history = [];
  }
}

function testWebhook(webhook) {
  webhookToTest.value = JSON.parse(JSON.stringify(webhook));
  testEvent.value = webhook.events.length > 0 ? webhook.events[0] : '';
  testPayload.value = webhook.defaultPayload || JSON.stringify({
    event: testEvent.value,
    timestamp: new Date().toISOString(),
    data: { message: "Teste de webhook" }
  }, null, 2);
  testStatus.value = 'preparing';
  showTestModal.value = true;
}

function executeTestWebhook() {
  // Em um caso real, você enviaria uma requisição HTTP
  testStatus.value = 'loading';
  
  // Simulação de envio
  setTimeout(() => {
    // Simulando sucesso ou erro aleatoriamente
    const success = Math.random() > 0.3;
    
    if (success) {
      testStatus.value = 'success';
      testResult.value = {
        status: 200,
        duration: Math.floor(Math.random() * 500) + 100,
        response: JSON.stringify({ success: true, message: "Webhook recebido com sucesso" }, null, 2)
      };
      
      // Adicionar ao histórico
      const historyEntry = {
        timestamp: new Date(),
        event: testEvent.value,
        status: 200,
        duration: testResult.value.duration,
        payload: testPayload.value
      };
      
      // Se o webhook sendo testado for o atual em edição, adiciona ao histórico
      if (editMode.value && currentWebhook.value.id === webhookToTest.value.id) {
        if (!currentWebhook.value.history) {
          currentWebhook.value.history = [];
        }
        currentWebhook.value.history.unshift(historyEntry);
      }
      
      // Adiciona ao histórico do webhook na lista principal
      const index = webhooks.value.findIndex(w => w.id === webhookToTest.value.id);
      if (index !== -1) {
        if (!webhooks.value[index].history) {
          webhooks.value[index].history = [];
        }
        webhooks.value[index].history.unshift(historyEntry);
      }
    } else {
      testStatus.value = 'error';
      testResult.value = {
        status: Math.random() > 0.5 ? 404 : 500,
        duration: Math.floor(Math.random() * 1000) + 200,
        error: Math.random() > 0.5 ? "Endpoint não encontrado" : "Erro interno do servidor",
        response: JSON.stringify({ success: false, error: "Recurso não encontrado" }, null, 2)
      };
      
      // Adicionar ao histórico
      const historyEntry = {
        timestamp: new Date(),
        event: testEvent.value,
        status: testResult.value.status,
        duration: testResult.value.duration,
        error: testResult.value.error,
        payload: testPayload.value
      };
      
      // Se o webhook sendo testado for o atual em edição, adiciona ao histórico
      if (editMode.value && currentWebhook.value.id === webhookToTest.value.id) {
        if (!currentWebhook.value.history) {
          currentWebhook.value.history = [];
        }
        currentWebhook.value.history.unshift(historyEntry);
      }
      
      // Adiciona ao histórico do webhook na lista principal
      const index = webhooks.value.findIndex(w => w.id === webhookToTest.value.id);
      if (index !== -1) {
        if (!webhooks.value[index].history) {
          webhooks.value[index].history = [];
        }
        webhooks.value[index].history.unshift(historyEntry);
      }
    }
  }, 1500);
}

// Lifecycle hooks
onMounted(() => {
  // Inicializar a visualização
  currentView.value = 'list';
});

const clinicStore = useClinicStore();
clinicStore.initialize();
</script>

<style scoped>
.animate-fadeIn {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Transições suaves */
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 300ms;
}

/* Ajustes para melhorar a responsividade */
@media (max-width: 640px) {
  .text-xl {
    font-size: 1.1rem;
  }
}

.clinic-btn {
  background: var(--clinic-btn-bg) !important;
  color: #fff !important;
  transition: background 0.2s;
}
.clinic-btn:hover, .clinic-btn:focus {
  background: var(--clinic-btn-bg-hover) !important;
}
.clinic-tab-active {
  color: var(--clinic-icon-color) !important;
  border-bottom: 2px solid var(--clinic-icon-color) !important;
}
.clinic-tab-inactive {
  color: var(--clinic-font-color) !important;
}
.clinic-header {
  color: var(--clinic-font-color) !important;
}
</style>